import type { BaseShow, SelectedNetwork, ShowFilter } from '@/types';

export const PLATFORM_FILTERS = [
  { id: 'acast', label: 'Acast (podcast)' },
  { id: 'youtube', label: 'YouTube' },
  { id: 'instagram', label: 'Instagram' },
  { id: 'tiktok', label: 'TikTok' },
  // Add more platforms as needed
];
/**
 * This function is used to get the show param.
 * @param selectedShow - The selected show.
 * @returns The show param.
 */
export const getShowParam = (selectedShow: ShowFilter): string => {
  return selectedShow !== 'all' && selectedShow ? selectedShow : 'all';
};

/**
 * Aggregates the shows of every selected network into a single deduped list, preserving network
 * (and within a network, show) order. Used by the multi-network Content Performance filter so the
 * shows filter and the `?shows=` URL validation share one source of truth across networks.
 * @param selectedNetworks - The networks currently selected via `?networks=`.
 * @returns The deduped shows across all selected networks, in URL order.
 */
export const aggregateShows = (selectedNetworks: SelectedNetwork[]): BaseShow[] => {
  const seenIds = new Set<string>();
  return selectedNetworks
    .flatMap((network) => network.shows ?? [])
    .filter((show) => {
      if (seenIds.has(show.id)) return false;
      seenIds.add(show.id);
      return true;
    });
};

/**
 * Returns whether any selected network contains an HLS-enabled show.
 * @param selectedNetworks - The networks currently selected by the user.
 */
export const hasHlsEnabledShow = (
  selectedNetworks: SelectedNetwork[],
  selectedShowIds?: string[]
): boolean =>
  selectedNetworks.some((network) =>
    selectedShowIds?.length
      ? network.shows?.some((show) => show.hls_enabled && selectedShowIds.includes(show.id))
      : network.shows?.some((show) => show.hls_enabled)
  );
